import * as Sentry from "@sentry/react";

// Sentry must init before React renders. globalHandlersIntegration (default)
// captures unhandled errors and unhandled promise rejections automatically.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
  release: "kapio-desktop@0.1.1",
  environment: import.meta.env.PROD ? "production" : "development",
  beforeSend(event) {
    if (event.extra) {
      delete event.extra.token;
      delete event.extra.password;
    }
    return event;
  },
});

import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { C, FONT } from "./lib/theme";
import { queryClient } from "./lib/queryClient";

// Global resets + keyframes that can't live in component style objects
const globalCss = `
  *{box-sizing:border-box}
  html,body,#root{margin:0;padding:0;min-height:100vh;background:${C.bg};color:${C.charcoal};font-family:${FONT};-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale}
  body{font-size:14px;line-height:1.6;font-weight:400}
  ::-webkit-scrollbar{width:10px;height:10px}
  ::-webkit-scrollbar-thumb{background:rgba(196,193,183,.5);border-radius:5px}
  .no-scrollbar::-webkit-scrollbar{display:none}
  .no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}
  .kbd{font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace;font-size:10px;font-weight:600;padding:2px 6px;border-radius:5px;background:${C.cream};border:1px solid rgba(196,193,183,.5);border-bottom-width:2px;color:${C.slate};margin-left:4px}
  .mono{font-family:ui-monospace,'SF Mono',Menlo,Consolas,monospace}
  @keyframes kapioPulse{0%,100%{opacity:1}50%{opacity:.45}}
  .pulse{animation:kapioPulse 2s infinite}
  @keyframes kapioSlideIn{from{opacity:0;transform:translateX(12px)}to{opacity:1;transform:none}}
  .slideIn{animation:kapioSlideIn .35s ease both}
  @keyframes kapioShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
  input,button,textarea,select{font-family:inherit}
  button:disabled{cursor:not-allowed}
`;

const styleEl = document.createElement("style");
styleEl.textContent = globalCss;
document.head.appendChild(styleEl);

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          fontFamily: FONT,
          background: C.bg,
          gap: 16,
          padding: 32,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <h2 style={{ color: C.charcoal, margin: 0, fontWeight: 800 }}>Something went wrong</h2>
          <p style={{ color: C.muted, margin: 0, fontSize: 14, maxWidth: 360 }}>
            {error?.message || "An unexpected error occurred. The error has been reported."}
          </p>
          <button
            onClick={resetError}
            style={{
              padding: "10px 24px",
              background: C.hunter,
              color: C.white,
              border: "none",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: FONT,
            }}
          >
            Try Again
          </button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
